import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../../components/Config/api.service';
import { canModifierDetailsPaiement } from '../../../components/Auth/permissions';
import LoadingBarWithStyles from '../../../components/Utils/LoadingBarWithStyles';
import { FaSearch, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const Recapitulatif = ({ 
  hasExcel,
  hasWord, 
  selectedMenuName,
  reportName,
  isCustom,
  totals,
  monthlyGrid,
  id, 
  paymentDetails, 
  setPaymentDetails,
  dateFrom,
  dateTo,
}) => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.resolvedLanguage === 'en' ? 'en-US' : 'fr-FR';
  const [isLoading, setIsLoading] = useState(false);
  const [editDetails, setEditDetails] = useState(paymentDetails || {});
  
  // UX Optimization states for Grid Previews
  const [gridSearch, setGridSearch] = useState('');
  const [gridPage, setGridPage] = useState(1);
  const [gridPageSize, setGridPageSize] = useState(25);

  // `/update-payment-details` est réservée à l'admin par `require_group` côté
  // Odoo : un rh lit le bloc, sans pouvoir rien y saisir ni enregistrer.
  const peutModifierDetails = canModifierDetailsPaiement();

  const monthOrder = ['jan', 'fev', 'mars', 'avril', 'mai', 'juin', 'juil', 'aout', 'sept', 'oct', 'nov', 'dec'];
  const showPaymentDetails =
    paymentDetails &&
    selectedMenuName == 'ETAT 1024 (1)' &&
    dateFrom &&
    dateTo &&
    (new Date(dateTo) - new Date(dateFrom)) >= 365 * 24 * 60 * 60 * 1000;

  const orderedPaymentDetails = monthOrder
  .filter(month => paymentDetails[month])
  .map(month => ({
    month,
    ...paymentDetails[month]
  }));

  useEffect(() => {
    setEditDetails(paymentDetails || {});
  }, [paymentDetails]);

  // Filtering & Pagination calculation for large custom report datasets
  const allGridRows = useMemo(() => monthlyGrid?.rows || [], [monthlyGrid]);

  const filteredGridRows = useMemo(() => {
    if (!gridSearch.trim()) return allGridRows;
    const q = gridSearch.toLowerCase();
    return allGridRows.filter(r =>
      (r.label || '').toLowerCase().includes(q) ||
      (r.sub_label || '').toLowerCase().includes(q)
    );
  }, [allGridRows, gridSearch]);

  const totalGridPages = gridPageSize === 0 ? 1 : (Math.ceil(filteredGridRows.length / gridPageSize) || 1);

  const currentGridRows = useMemo(() => {
    if (gridPageSize === 0) return filteredGridRows;
    const start = (gridPage - 1) * gridPageSize;
    return filteredGridRows.slice(start, start + gridPageSize);
  }, [filteredGridRows, gridPage, gridPageSize]);

  useEffect(() => {
    setGridPage(1);
  }, [gridSearch, monthlyGrid]);

  const handlePrintPDF = () => {
    if (!id) return;

    setIsLoading(true);
    apiService
      .getBlobFile(`/financial-statement/pdf/${selectedMenuName}/${id}`)
      .then((response) => {
        if (response && response.data) {
          const fileName = `Etat_${selectedMenuName}.pdf`;
          const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
      })
      .catch((error) => {
        console.error('Erreur lors du téléchargement du PDF:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDownloadExcel = () => {
    if (!id) return;
  
    setIsLoading(true);
    const endpoint = isCustom 
      ? `/financial-statement/xls/${reportName || selectedMenuName}/${id}` 
      : `/financial-statement/xls/${selectedMenuName} Excel/${id}`;
    
    apiService
      .getBlobFile(endpoint)
      .then((response) => {
        if (response && response.data) {
          const fileName = `Etat_${selectedMenuName}.xlsx`;
          const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
      })
      .catch((error) => {
        console.error('Erreur lors du téléchargement du fichier Excel:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDownloadWord = () => {
    if (!id) return;

    setIsLoading(true);
    apiService
      .getBlobFile(`/financial-statement/docx/${selectedMenuName} Word/${id}`)
      .then((response) => {
        if (response && response.data) {
          const fileName = `Etat_${selectedMenuName}.docx`;
          const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
      })
      .catch((error) => {
        console.error('Erreur lors du téléchargement du fichier Word:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handlePaymentDetailChange = (month, field, value) => {
    setEditDetails(prev => ({
      ...prev,
      [month]: { ...prev[month], [field]: value }
    }));
  };

  const handleSavePaymentDetails = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      await apiService.saveElement('/update-payment-details', {
        report_id: id,
        payment_details: editDetails
      });
    } catch (err) {
      console.error('Erreur sauvegarde détails:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  function getFrenchMonth(mois) {
    const map = {
      jan: t('recapitulatif.january'), fev: t('recapitulatif.february'), mars: t('recapitulatif.march'),
      avril: t('recapitulatif.april'), mai: t('recapitulatif.may'), juin: t('recapitulatif.june'),
      juil: t('recapitulatif.july'), aout: t('recapitulatif.august'), sept: t('recapitulatif.september'),
      oct: t('recapitulatif.october'), nov: t('recapitulatif.november'), dec: t('recapitulatif.december')
    };
    return map[mois] || mois;
  }

  function getLastDayOfMonth(month, dateFrom, dateTo) {
    const year = dateFrom ? new Date(dateFrom).getFullYear() : new Date().getFullYear();
    const monthMap = {
      jan: 0, fev: 1, mars: 2, avril: 3, mai: 4, juin: 5,
      juil: 6, aout: 7, sept: 8, oct: 9, nov: 10, dec: 11
    };
    const m = monthMap[month];
    if (m === undefined) return '';
    const lastDay = new Date(year, m + 1, 0);
    return lastDay.toISOString().slice(0, 10);
  }

  const startRowIndex = filteredGridRows.length === 0 ? 0 : (gridPageSize === 0 ? 1 : (gridPage - 1) * gridPageSize + 1);
  const endRowIndex = gridPageSize === 0 ? filteredGridRows.length : Math.min(gridPage * gridPageSize, filteredGridRows.length);

  return (
    <div className="font-sans max-w-6xl mx-auto p-2">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">{selectedMenuName || t('financialStatements.summary')}</h2>
        <div className="flex gap-2">
          {hasWord && (
            <button
              className="btn rounded-full bg-[--color-primary] text-white hover:bg-[--bg-hover]"
              onClick={handleDownloadWord}
            >
              {t('recapitulatif.downloadWord')}
            </button>
          )}
          {hasExcel && (
            <button
              className="btn rounded-full bg-[--color-primary] text-white hover:bg-[--bg-hover]"
              onClick={handleDownloadExcel}
            >
              {t('recapitulatif.downloadExcel')}
            </button>
          )}
          {!isCustom && (
            <button
              className="btn bg-[--bg-color] text-[--color-primary] rounded-full"
              onClick={handlePrintPDF}
            >
              {t('recapitulatif.downloadPdf')}
            </button>
          )}
        </div>
      </div>

      {isLoading && <LoadingBarWithStyles />}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 mt-2">
        {totals &&
          Object.entries(totals).map(([key, value]) => (
            <div key={key} className="bg-[--bg-color] shadow-md rounded-lg p-6">
              <h2 className="text-3xl font-bold text-[--color-primary] mb-3">
                {value.toLocaleString(dateLocale, { style: 'currency', currency: 'XOF' })}
              </h2>
              <p className="text-gray-700">{key.replace(/_/g, ' ')}</p>
            </div>
          ))}
      </div>

      {/* Dynamic Grid Preview for Custom Reports with UX Optimization */}
      {monthlyGrid && monthlyGrid.rows && monthlyGrid.rows.length > 0 && (
        <div className="mb-6 bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4 border-b border-gray-100 pb-3">
            <div>
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <span>📊</span>
                <span>
                  {monthlyGrid.aggregation_type === 'per_month'
                    ? 'Aperçu : Tableau Croisé Mensuel'
                    : monthlyGrid.aggregation_type === 'per_employee'
                    ? 'Aperçu : Cumulé par Employé'
                    : monthlyGrid.aggregation_type === 'total'
                    ? 'Aperçu : Totaux Période'
                    : 'Aperçu : Rapport Détaillé'}
                </span>
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                {t('recapitulatif.fullExcelNotice')}
              </p>
            </div>

            {/* Quick Filter & Rows Count */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative min-w-[220px]">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                <input
                  type="text"
                  placeholder={t('recapitulatif.searchPreviewPlaceholder')}
                  value={gridSearch}
                  onChange={e => setGridSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--color-primary] focus:border-transparent"
                />
              </div>

              {/* Rows Per Page Selector */}
              {allGridRows.length > 25 && (
                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                  <select
                    value={gridPageSize}
                    onChange={e => setGridPageSize(Number(e.target.value))}
                    className="border border-gray-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-[--color-primary]"
                  >
                    <option value={25}>25 {t('recapitulatif.perPage')}</option>
                    <option value={50}>50 {t('recapitulatif.perPage')}</option>
                    <option value={100}>100 {t('recapitulatif.perPage')}</option>
                    <option value={0}>{t('recapitulatif.allRows')}</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Rows Info Badge */}
          <div className="mb-2 flex items-center justify-between text-xs text-gray-500">
            <span>
              {t('recapitulatif.showingRowsInfo', {
                start: startRowIndex,
                end: endRowIndex,
                total: filteredGridRows.length
              })}
            </span>
          </div>

          {/* Scrollable Container with Sticky Table Headers */}
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto rounded-lg shadow-inner border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 bg-white">
              <thead className="bg-[--color-primary] text-white sticky top-0 z-10 shadow-sm">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold rounded-tl-lg bg-[--color-primary]">
                    {monthlyGrid.aggregation_type === 'per_month'
                      ? 'Élément / Rubrique'
                      : 'Employé / Intitulé'}
                  </th>
                  {monthlyGrid.aggregation_type !== 'per_month' && (
                    <th className="px-4 py-3 text-left text-sm font-semibold bg-[--color-primary]">Période / Date</th>
                  )}
                  {monthlyGrid.aggregation_type === 'per_month'
                    ? (monthlyGrid.months || []).map((m, idx) => (
                        <th key={idx} className="px-4 py-3 text-right text-sm font-semibold bg-[--color-primary]">{m}</th>
                      ))
                    : (monthlyGrid.column_headers || []).map((h, idx) => (
                        <th key={idx} className="px-4 py-3 text-right text-sm font-semibold bg-[--color-primary]">{h}</th>
                      ))
                  }
                  <th className="px-4 py-3 text-right text-sm font-bold rounded-tr-lg bg-[--color-primary]">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 text-sm">
                {currentGridRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={
                        (monthlyGrid.aggregation_type === 'per_month'
                          ? (monthlyGrid.months || []).length
                          : (monthlyGrid.column_headers || []).length + 1) + 2
                      }
                      className="text-center py-8 text-gray-500 italic"
                    >
                      {t('customReportManager.noReportFound')}
                    </td>
                  </tr>
                ) : (
                  currentGridRows.map((row, rIdx) => (
                    <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50/50 hover:bg-gray-100/50'}>
                      <td className="px-4 py-2.5 font-medium text-gray-900">{row.label}</td>
                      {monthlyGrid.aggregation_type !== 'per_month' && (
                        <td className="px-4 py-2.5 text-gray-500 text-xs font-mono">{row.sub_label || ''}</td>
                      )}
                      {row.values && row.values.map((v, vIdx) => (
                        <td key={vIdx} className="px-4 py-2.5 text-right text-gray-700 font-mono">
                          {(v || 0).toLocaleString(dateLocale, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                        </td>
                      ))}
                      <td className="px-4 py-2.5 text-right font-bold text-[--color-primary] font-mono">
                        {(row.total || 0).toLocaleString(dateLocale, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {(monthlyGrid.month_totals || monthlyGrid.col_totals) && (
                <tfoot className="bg-gray-100 font-bold border-t-2 border-gray-300 sticky bottom-0 z-10">
                  <tr>
                    <td className="px-4 py-3 text-gray-900">Totaux Global Période</td>
                    {monthlyGrid.aggregation_type !== 'per_month' && <td className="px-4 py-3"></td>}
                    {(monthlyGrid.month_totals || monthlyGrid.col_totals).map((mt, mtIdx) => (
                      <td key={mtIdx} className="px-4 py-3 text-right text-gray-900 font-mono">
                        {(mt || 0).toLocaleString(dateLocale, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right text-[--color-primary] font-mono">
                      {(monthlyGrid.grand_total || 0).toLocaleString(dateLocale, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Pagination Footer */}
          {gridPageSize > 0 && totalGridPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100 text-xs">
              <span className="text-gray-500 font-medium">
                Page {gridPage} / {totalGridPages}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setGridPage(p => Math.max(1, p - 1))}
                  disabled={gridPage === 1}
                  className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FaChevronLeft size={10} />
                  Précédent
                </button>
                <button
                  onClick={() => setGridPage(p => Math.min(totalGridPages, p + 1))}
                  disabled={gridPage === totalGridPages}
                  className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Suivant
                  <FaChevronRight size={10} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Payment Details */}
      {showPaymentDetails && (
        <div className="mb-6">
          <h3 className="text-xl font-bold mb-4">{t('recapitulatif.monthlyPaymentDetails')}</h3>
          <div className="overflow-x-auto rounded-lg shadow">
            <table className="min-w-full bg-white border border-gray-200 rounded-lg">
              <thead className="bg-[--color-primary] text-white">
                <tr>
                  <th className="px-4 py-3 text-left rounded-tl-lg">{t('payslipsList.month')}</th>
                  <th className="px-4 py-3 text-left">{t('recapitulatif.date')}</th>
                  <th className="px-4 py-3 text-left">{t('recapitulatif.bordereau')}</th>
                  <th className="px-4 py-3 text-left">IR</th>
                  <th className="px-4 py-3 text-left">TRIMF</th>
                  <th className="px-4 py-3 text-left rounded-tr-lg">CFCE</th>
                </tr>
              </thead>
              <tbody>
                {orderedPaymentDetails.map(details => {
                  const edited = editDetails[details.month] || {};
                  return (
                    <tr key={details.month}>
                      <td className="px-4 py-2 font-semibold capitalize">{getFrenchMonth(details.month)}</td>
                      <td className="px-4 py-2">
                        <input
                          type="date"
                          value={edited.date || getLastDayOfMonth(details.month, dateFrom, dateTo)}
                          onChange={e => handlePaymentDetailChange(details.month, 'date', e.target.value)}
                          readOnly={!peutModifierDetails}
                          className="border rounded px-3 py-2 w-full focus:ring focus:ring-[--color-primary]"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          value={edited.bordereau || ''}
                          onChange={e => handlePaymentDetailChange(details.month, 'bordereau', e.target.value)}
                          readOnly={!peutModifierDetails}
                          className="border rounded px-3 py-2 w-full focus:ring focus:ring-[--color-primary]"
                          placeholder={t('recapitulatif.bordereauNumber')}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          value={edited.ir || ''}
                          onChange={e => handlePaymentDetailChange(details.month, 'ir', e.target.value)}
                          readOnly={!peutModifierDetails}
                          className="border rounded px-3 py-2 w-full focus:ring focus:ring-[--color-primary]"
                          min={0}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          value={edited.trimf || ''}
                          onChange={e => handlePaymentDetailChange(details.month, 'trimf', e.target.value)}
                          readOnly={!peutModifierDetails}
                          className="border rounded px-3 py-2 w-full focus:ring focus:ring-[--color-primary]"
                          min={0}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          value={edited.cfce || ''}
                          onChange={e => handlePaymentDetailChange(details.month, 'cfce', e.target.value)}
                          readOnly={!peutModifierDetails}
                          className="border rounded px-3 py-2 w-full focus:ring focus:ring-[--color-primary]"
                          min={0}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {peutModifierDetails && (
            <button
              className="btn mt-6 bg-[--color-primary] text-white rounded-full px-6 py-3 shadow hover:bg-[--bg-hover] transition"
              onClick={handleSavePaymentDetails}
              disabled={isLoading}
            >
              {t('recapitulatif.saveChanges')}
            </button>
          )}
        </div>
      )}      
    </div>
  );
};

export default Recapitulatif;